import { AVATAR_COLORS, DEFAULT_AVATAR } from "@constants/global";
import Avatar from "boring-avatars";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import { PostAuthor } from "components/ui/v2/post-author";
import { linkifyText } from "utils/string";
import React from "react";
import { IUser } from "types/User.types";

export interface EventCardUser {
  id?: string;
  profileImage?: string;
  name: string;
  previewUser?: Partial<IUser>;
}

export interface EventCardProps {
  /** Theme color as "r,g,b" string, e.g. "236,64,122" */
  colorRgb: string;
  /** Tailwind text-color class, e.g. "text-rose-500" */
  colorTextClass: string;
  /** Optional sender shown top-left (post author) */
  sender?: EventCardUser;
  /** When provided, renders a time-ago stamp below the sender name */
  senderCreatedAt?: Date;
  /** Small icon for the title row. Omit (with titleLabel) for a title-less card. */
  titleIcon?: React.ReactNode;
  /** Label text next to the title icon. Omit to hide the title row entirely. */
  titleLabel?: string;
  /** Single hero avatar */
  hero?: EventCardUser;
  /** Avatar size in px (default 96) */
  heroSize?: number;
  /** Node positioned bottom-right of the hero ring (e.g. years badge) */
  heroOverlay?: React.ReactNode;
  /** Multiple hero avatars shown as an overlapping stack */
  heroes?: EventCardUser[];
  /** Total count for the +N overflow chip when heroes > 5 */
  heroesTotal?: number;
  /** Name displayed below the hero */
  heroName?: string;
  /** Role displayed below heroName */
  heroRole?: string;
  /** Pill badge shown below role */
  badge?: React.ReactNode;
  /** Italic quote text */
  quote?: string;
  /**
   * Renders the particle at position `index` with the given `size` (px).
   * When provided, each sparkle slot renders this content instead of a dot.
   * Cycle through different icons/emojis using `index % yourArray.length`.
   */
  particleRenderer?: (index: number, size: number) => React.ReactNode;
  /** Additional content rendered at the bottom */
  children?: React.ReactNode;
  /** Compact layout for stacked/side-by-side views */
  compact?: boolean;
}

/* Sparkle slot definitions.
   dotSize  = colored-circle radius (fallback)
   iconSize = font-size / svg-size when particleRenderer is provided */
/* Positions orbit the hero photo center (≈ top 22%, left 50%).
   Angles spread 360° at two radii so they look like bursting from the person. */
const SPARKLES = [
  { top: "3%",  left: "58%", dotSize: 6,  iconSize: 17 },
  { top: "8%",  left: "40%", dotSize: 4,  iconSize: 12 },
  { top: "9%",  left: "26%", dotSize: 6,  iconSize: 16 },
  { top: "22%", left: "17%", dotSize: 4,  iconSize: 12 },
  { top: "33%", left: "21%", dotSize: 6,  iconSize: 15 },
  { top: "39%", left: "34%", dotSize: 4,  iconSize: 11 },
  { top: "40%", left: "64%", dotSize: 6,  iconSize: 15 },
  { top: "30%", left: "77%", dotSize: 4,  iconSize: 13 },
  { top: "22%", left: "81%", dotSize: 6,  iconSize: 17 },
  { top: "13%", left: "68%", dotSize: 4,  iconSize: 13 },
] as const;

/** Pseudo-random estable por índice (0–1). */
function sparkleUnit(index: number, salt = 0): number {
  const x = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

interface SparkleMotion {
  opacityMin: number;
  opacityMid: number;
  opacityMax: number;
  scaleMin: number;
  scaleMid: number;
  scaleMax: number;
  baseSizeMult: number;
  duration: number;
  delay: number;
  dotOpacity: number;
  orbitRx: number;
  orbitRy: number;
  orbitStart: number;
  orbitDir: 1 | -1;
}

function buildSparkleMotion(index: number): SparkleMotion {
  const r = (salt: number) => sparkleUnit(index, salt);
  return {
    opacityMin: 0.02 + r(1) * 0.06,   // 0.02–0.08
    opacityMid: 0.04 + r(2) * 0.11,   // 0.04–0.15
    opacityMax: 0.06 + r(3) * 0.18,   // 0.06–0.24 (wide range, very random)
    scaleMin:   0.55 + r(4) * 0.12,
    scaleMid:   0.95 + r(5) * 0.18,
    scaleMax:   1.28 + r(6) * 0.52,
    baseSizeMult: 0.55 + r(14) * 1.30, // 0.55–1.85 — big size variation per particle
    duration: 4.2 + r(7) * 5.4,
    delay: r(8) * 2.4,
    dotOpacity: 0.04 + r(9) * 0.07,
    orbitRx: 4 + r(10) * 9,
    orbitRy: 3 + r(11) * 8,
    orbitStart: r(12) * Math.PI * 2,
    orbitDir: r(13) > 0.5 ? 1 : -1,
  };
}

const SPARKLE_MOTIONS = SPARKLES.map((_, i) => buildSparkleMotion(i));

function sparkleOrbitTransform(m: SparkleMotion, t: number, scale: number): string {
  const angle = m.orbitStart + t * Math.PI * 2 * m.orbitDir;
  const x = Math.cos(angle) * m.orbitRx;
  const y = Math.sin(angle) * m.orbitRy;
  return `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${scale})`;
}

function buildSparkleKeyframes(motions: SparkleMotion[]): string {
  const steps: Array<{ t: number; scale: keyof Pick<SparkleMotion, "scaleMin" | "scaleMid" | "scaleMax">; opacity: keyof Pick<SparkleMotion, "opacityMin" | "opacityMid" | "opacityMax"> }> = [
    { t: 0, scale: "scaleMin", opacity: "opacityMin" },
    { t: 0.28, scale: "scaleMid", opacity: "opacityMid" },
    { t: 0.52, scale: "scaleMax", opacity: "opacityMax" },
    { t: 0.76, scale: "scaleMid", opacity: "opacityMid" },
    { t: 1, scale: "scaleMin", opacity: "opacityMin" },
  ];

  return motions
    .map((m, i) => {
      const frames = steps
        .map((step, idx) => {
          const pct = idx === 0 ? "0%, 100%" : `${Math.round(step.t * 100)}%`;
          const transform = sparkleOrbitTransform(m, step.t, m[step.scale]);
          const opacity = m[step.opacity];
          return `          ${pct} { transform: ${transform}; opacity: ${opacity}; }`;
        })
        .join("\n");

      return `
        @keyframes ec-sp-${i} {
${frames}
        }`;
    })
    .join("");
}

function UserAvatar({ user, size }: { user: EventCardUser; size: number }) {
  return user.profileImage === DEFAULT_AVATAR ? (
    <Avatar size={size} name={user.name} variant="beam" colors={AVATAR_COLORS} />
  ) : (
    <AvatarWithInitials urlImg={user.profileImage} initials={user.name} customSize={size} />
  );
}

const EventCard: React.FC<EventCardProps> = ({
  colorRgb,
  colorTextClass,
  sender,
  senderCreatedAt,
  titleIcon,
  titleLabel,
  hero,
  heroSize,
  heroOverlay,
  heroes,
  heroesTotal,
  heroName,
  heroRole,
  badge,
  quote,
  particleRenderer,
  children,
  compact = false,
}) => {
  const resolvedHeroSize = heroSize ?? (compact ? 72 : 96);
  const isMulti = (heroes?.length ?? 0) > 0;

  return (
    <div className={`relative flex flex-col items-center overflow-hidden ${compact ? "pb-3 pt-3" : "pb-6 pt-5"}`}>
      {/* ── Keyframes ─────────────────────────────────────────────────────── */}
      <style>{`
        ${buildSparkleKeyframes(SPARKLE_MOTIONS)}
        @keyframes ec-float { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-5px)} }
        @keyframes ec-glow  { 0%,100%{transform:scale(1.9);opacity:0.18} 50%{transform:scale(2.3);opacity:0.32} }
        @keyframes ec-ring  {
          0%,100%{box-shadow:0 0 0 0 var(--ec-c0),0 0 10px 2px var(--ec-c16);}
          50%    {box-shadow:0 0 0 5px var(--ec-c8),0 0 22px 6px var(--ec-c28);}
        }
      `}</style>

      {/* Radial tint */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0"
        style={{
          height: "180px",
          background: `radial-gradient(ellipse 90% 100% at 50% 0%, rgba(${colorRgb},0.10) 0%, transparent 100%)`,
        }}
      />

      {/* Sparkle particles */}
      {SPARKLES.map((s, i) => {
        const hasParticle = !!particleRenderer;
        const motion = SPARKLE_MOTIONS[i];
        const anim = `ec-sp-${i} ${motion.duration}s ease-in-out ${motion.delay}s infinite both`;
        const initialTransform = sparkleOrbitTransform(motion, 0, motion.scaleMin);
        const size = Math.round(s.iconSize * motion.baseSizeMult);

        return (
          <div
            key={i}
            aria-hidden
            className="pointer-events-none absolute will-change-[transform,opacity]"
            style={{
              top: s.top,
              left: s.left,
              opacity: motion.opacityMin,
              transform: initialTransform,
              ...(hasParticle
                ? { lineHeight: 1, fontSize: size }
                : {
                    width: Math.round(s.dotSize * motion.baseSizeMult),
                    height: Math.round(s.dotSize * motion.baseSizeMult),
                    borderRadius: "9999px",
                    background: `rgba(${colorRgb},${motion.dotOpacity})`,
                  }),
              animation: anim,
              transformOrigin: "center",
            }}
          >
            {hasParticle ? particleRenderer(i, size) : null}
          </div>
        );
      })}

      {/* Sender — in-flow at the top so title row never overlaps */}
      {sender && (
        <div className={`relative z-10 w-full px-4 ${compact ? "mb-2" : "mb-3"}`}>
          <PostAuthor
            profileImage={sender.profileImage}
            name={sender.name}
            createdAt={senderCreatedAt}
            avatarSize={30}
            userId={sender.id}
            user={sender.previewUser}
          />
        </div>
      )}

      {/* Title row — omitted entirely when no label/icon is provided */}
      {(titleLabel || titleIcon) && (
        <div className={`relative z-10 flex items-center gap-1.5 ${compact ? "mb-2 mt-0" : "mb-5 mt-1"}`}>
          {titleIcon && <span className="flex items-center">{titleIcon}</span>}
          {titleLabel && (
            <span className={`text-sm font-semibold tracking-wide ${colorTextClass}`}>
              {titleLabel}
            </span>
          )}
        </div>
      )}

      {/* Hero — multiple avatars stacked */}
      {isMulti && (
        <div
          className={`relative z-10 w-full ${compact ? "mb-2" : "mb-4"}`}
          style={{ animation: "ec-float 4.5s ease-in-out infinite" }}
        >
          <div className="flex items-center justify-center">
            {heroes!.slice(0, 5).map((u, i) => (
              <div
                key={i}
                className="rounded-full border-[3px] border-white"
                style={{
                  marginLeft: i > 0 ? "-18px" : 0,
                  zIndex: 10 - i,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                }}
              >
                <UserAvatar user={u} size={64} />
              </div>
            ))}
            {(heroesTotal ?? 0) > 5 && (
              <div
                className={`flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-white text-sm font-bold ${colorTextClass}`}
                style={{
                  marginLeft: "-18px",
                  zIndex: 5,
                  background: `rgba(${colorRgb},0.10)`,
                }}
              >
                +{(heroesTotal ?? 0) - 5}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero — single avatar with animated gradient ring */}
      {!isMulti && hero && (
        <div
          className={`relative z-10 ${compact ? "mb-2" : "mb-4"}`}
          style={{ animation: "ec-float 4s ease-in-out infinite" }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, rgba(${colorRgb},0.22) 0%, transparent 70%)`,
              animation: "ec-glow 3.5s ease-in-out infinite",
            }}
          />
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              padding: "3px",
              background: `linear-gradient(135deg, rgba(${colorRgb},0.42) 0%, rgba(${colorRgb},0.65) 50%, rgba(${colorRgb},0.28) 100%)`,
              animation: "ec-ring 3.5s ease-in-out infinite",
              "--ec-c0":  `rgba(${colorRgb},0.00)`,
              "--ec-c8":  `rgba(${colorRgb},0.08)`,
              "--ec-c16": `rgba(${colorRgb},0.16)`,
              "--ec-c28": `rgba(${colorRgb},0.28)`,
            } as React.CSSProperties}
          >
            <div className="rounded-full bg-white p-0.5">
              <UserAvatar user={hero} size={resolvedHeroSize} />
            </div>
          </div>
          {heroOverlay && (
            <div className="absolute -bottom-1 -right-1 z-10">{heroOverlay}</div>
          )}
        </div>
      )}

      {/* Hero name */}
      {heroName && (
        <h3 className={`relative z-10 text-center font-bold capitalize leading-tight tracking-tight text-gray-900 dark:text-gray-100 ${compact ? "text-[14px]" : "text-[21px]"}`}>
          {heroName}
        </h3>
      )}

      {/* Hero role */}
      {heroRole && (
        <span className={`relative z-10 text-center text-slate-500 dark:text-zinc-400 ${compact ? "mt-0.5 text-[11px]" : "mt-1 text-sm"}`}>
          {heroRole}
        </span>
      )}

      {/* Badge */}
      {badge && <div className={`relative z-10 ${compact ? "mt-1.5" : "mt-3"}`}>{badge}</div>}

      {/* Quote */}
      {quote && (
        <div className="relative z-10 w-full px-7 pb-1 pt-5 sm:px-10">
          <span
            aria-hidden
            className="pointer-events-none absolute left-5 top-3 select-none font-serif text-4xl leading-none sm:left-7"
            style={{ color: `rgba(${colorRgb},0.14)`, lineHeight: 1 }}
          >
            &ldquo;
          </span>
          <p className="relative break-words text-center text-[12px] font-medium italic leading-relaxed text-slate-700 dark:text-zinc-300 sm:text-[13px] sm:leading-[1.7]">
            {linkifyText(quote)}
          </p>
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-0 right-5 select-none font-serif text-4xl leading-none sm:right-7"
            style={{ color: `rgba(${colorRgb},0.14)`, lineHeight: 1 }}
          >
            &rdquo;
          </span>
        </div>
      )}

      {/* Extra content slot */}
      {children && <div className="relative z-10 w-full">{children}</div>}
    </div>
  );
};

export default EventCard;
